/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faSave } from '@fortawesome/pro-duotone-svg-icons';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { toast } from 'react-toastify';
import { updateAgent, getAgent, setTemporaryPassword } from '../../../../actions/agent';

import Detail from './Detail';
import AgencyDetail from '../AgencyDetail';
import Select from './ActiveSelect';
import Password from './Password';
import { Spinner, LoaderButton } from '../../../../common/components';

function Details({ user = {} }) {
  const dispatch = useDispatch();
  const {
    agents: {
      records: agents,
      selectedAgent,
    },
    user: profileuser,
    details: {
      saving,
      values: details,
      editing,
      erroring,
    },
    theme: {
      features: {
        npnAsSAN,
        agencies: showAgencies,
        useLinkedNPN,
        editAgentEmail,
      },
    },
  } = useSelector(store => store);
  const {
    can_edit_users: canEdit,
    is_admin: isAdmin,
  } = profileuser;

  useEffect(() => {
    dispatch({ type: 'LOAD_DETAILS', user });
  }, [user]);

  useEffect(() => {
    const self = agents.find((agent) => agent.username === selectedAgent.username);
    if (self) {
      dispatch({ type: 'LOAD_DETAILS', user: self });
    }
  }, [agents, user]);

  function handleSaveClick() {
    dispatch({ type: 'DETAILS_SAVING' });
    updateAgent(user.username, { ...details })
      .then(() => {
        getAgent(user.username).then(({ data }) => {
          dispatch({ type: 'LOAD_DETAILS', user: data });
        });
      })
      .then(() => {
        dispatch({ type: 'CLOSE_EDIT_DETAILS' });
        dispatch({ type: 'DETAILS_DONE_SAVING' });
      }).catch((err) => {
        if (err.response && err.response.status === 409) {
          toast.error('We are unable to update the email address as this email address is already in use.');
        } else {
          toast.error('Error updating Agent');
        }
        dispatch({ type: 'DETAILS_DONE_SAVING' });
      });
  }

  function handleCancelClick() {
    dispatch({ type: 'LOAD_DETAILS', user });
  }

  function handleChange({ target }) {
    const { checked, id } = target;
    dispatch({ type: 'EDIT_DETAILS', id, value: checked, valid: true });
  }

  return (
    <>
      <div className="d-flex justify-content-between mb-3">
        <div>
          <h5 className="font-weight-bold">
            Agent Details
            {editing && (
              <i className="font-weight-normal"> - edit</i>
            )}
          </h5>
        </div>
        <div className="align-self-end fade-in">
          {editing ? (
            <div>
              <button
                type="button"
                className="btn btn-danger me-3"
                onClick={() => handleCancelClick()}
              >
                <FontAwesomeIcon icon={faTimes} size="lg" className="me-2" />
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={() => handleSaveClick()}
                disabled={saving || erroring}
              >
                <FontAwesomeIcon icon={faSave} size="lg" className="me-2" />
                Save Changes
                {saving && (
                  <Spinner size="sm" className="ms-2" />
                )}
              </button>
            </div>
          ) : (
            canEdit && (
              <button
                type="button"
                className="btn btn-success px-4"
                onClick={() => dispatch({ type: 'OPEN_EDIT_DETAILS' })}
                disabled={saving || erroring}
              >
                <FontAwesomeIcon icon={faEdit} size="lg" className="me-2" />
                Edit
              </button>
            )
          )}
        </div>
      </div>
      <div className="fade-in">
        <form>
          <div className="row">
            <div className="col">
              <Detail id="first_name" pattern={/^.{1,20}$/i} capitalize label="First Name" />
              <Detail id="middle_name" pattern={/^.{0,20}$/i} capitalize label="Middle Name" />
              <Detail id="last_name" pattern={/^.{1,20}$/i} capitalize label="Last Name" />
              <Detail
                id="email"
                // @see https://emailregex.com/
                // eslint-disable-next-line max-len
                pattern={/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/}
                disabled={!editAgentEmail}
                label="Email"
              />
              <Detail id="mobile_phone" placeholder="e.g. (123) 456-7890" label="Cell Phone Number" />
              {(showAgencies && isAdmin) ? <AgencyDetail /> : null}
              <div className="font-large fw-bold mt-3">
                <label htmlFor="is_test_user" className="me-2 form-check-label">
                  QA User
                </label>
                <input
                  type="checkbox"
                  id="is_test_user"
                  className="form-check-control"
                  disabled={!editing || saving}
                  checked={details.is_test_user || false}
                  onChange={(e) => handleChange(e)}
                />
              </div>
            </div>
            <div className="col">
              <Detail id="npn" pattern={/^.{1,10}$/} label={npnAsSAN ? 'Agent SAN #' : 'Agent NPN'} />
              { useLinkedNPN ? (
                <Detail id="npn_linked" pattern={/^.{1,10}$/} label="Linked NPN #" />
              ) : null}
              <Select id="is_active" pattern={/^(Active|Inactive)$/} type="bool" label="User Status" />
              <Detail id="start_date" pattern={/^\d{4}-\d{2}-\d{2}$/} type="date" disabled label="Effective Date" />
              <Detail id="last_updated" disabled label="Last Update" />
              <Detail id="last_updated_by_name" disabled capitalize label="Last Update By" />
            </div>
          </div>
          <Password details={details} />
        </form>
      </div>
    </>
  );
}

export default Details;
